import { useEffect, useId, useState } from "react";
import clsx from "clsx";

const DEFAULT_MAX_QUICK = 4;

function parseCount(value: string): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

export interface PartyCountFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Highest value selectable via quick buttons (default 4). */
  maxQuickSelect?: number;
  className?: string;
}

export function PartyCountField({
  label,
  value,
  onChange,
  disabled = false,
  maxQuickSelect = DEFAULT_MAX_QUICK,
  className,
}: PartyCountFieldProps) {
  const inputId = useId();
  const count = parseCount(value);
  const [manualMode, setManualMode] = useState(count > maxQuickSelect);

  useEffect(() => {
    if (count > maxQuickSelect) setManualMode(true);
  }, [count, maxQuickSelect]);

  const showManual = manualMode || count > maxQuickSelect;
  const quickOptions = Array.from({ length: maxQuickSelect + 1 }, (_, i) => i);

  function selectQuick(n: number) {
    setManualMode(false);
    onChange(String(n));
  }

  function openManual() {
    setManualMode(true);
    if (count <= maxQuickSelect) {
      onChange(String(maxQuickSelect + 1));
    }
  }

  function onManualChange(raw: string) {
    if (raw === "") {
      onChange("");
      return;
    }
    const digits = raw.replace(/\D/g, "");
    onChange(digits);
  }

  return (
    <div className={clsx("text-sm", className)}>
      <span className="mb-1 block text-label-secondary">{label}</span>
      <div
        className="flex flex-wrap gap-1"
        role="group"
        aria-label={label}
      >
        {quickOptions.map((n) => {
          const selected = !showManual && count === n;
          return (
            <button
              key={n}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => selectQuick(n)}
              className={clsx(
                "min-w-[2.25rem] flex-1 rounded-lg border px-2 py-2 text-center text-sm font-medium transition sm:flex-none",
                selected
                  ? "border-accent bg-accent/10 ring-1 ring-accent text-label"
                  : "border-separator bg-fill-tertiary text-label hover:border-accent/50",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              {n}
            </button>
          );
        })}
        {!showManual ? (
          <button
            type="button"
            disabled={disabled}
            onClick={openManual}
            className={clsx(
              "min-w-[2.25rem] flex-1 rounded-lg border border-dashed border-separator bg-transparent px-2 py-2 text-center text-sm font-medium text-label-secondary transition sm:flex-none",
              "hover:border-accent/50 hover:text-label",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            5+
          </button>
        ) : null}
      </div>
      {showManual ? (
        <div className="mt-2 space-y-1">
          <label htmlFor={inputId} className="sr-only">
            {label} (manuell)
          </label>
          <input
            id={inputId}
            type="number"
            min={maxQuickSelect + 1}
            className="eh-input w-full"
            value={value}
            onChange={(e) => onManualChange(e.target.value)}
            disabled={disabled}
            inputMode="numeric"
          />
          {!disabled ? (
            <button
              type="button"
              className="text-xs text-accent hover:underline"
              onClick={() => {
                setManualMode(false);
                onChange(String(Math.min(count, maxQuickSelect)));
              }}
            >
              Schnellauswahl 0–{maxQuickSelect}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
